import type { UrlStatusUpdate } from '@kanbu/schema/contracts';
import { DocumentStatus } from '@kanbu/schema/enums';
import { Loader2 } from 'lucide-preact';
import { useEffect, useState } from 'preact/compat';

type Props = {
  urls: UrlStatusUpdate[];
};

type Phase = 'searching' | 'parsing' | 'embedding' | 'done';

const phaseMessages: Record<Phase, string[]> = {
  searching: [
    'Hledám stránky na vašem webu...',
    'Procházím strukturu webu...',
    'Mapuji dostupné URL adresy...',
  ],
  parsing: [
    'Načítám obsah stránek...',
    'Zpracovávám data z webu...',
    'Čtu a analyzuji obsah...',
    'Extrahuji informace...',
    'Parsuji webové stránky...',
  ],
  embedding: [
    'Vytvářím znalostní bázi...',
    'Učím se z vašeho obsahu...',
    'Trénuji AI na vašich datech...',
    'Zpracovávám znalosti...',
    'Připravuji inteligentní odpovědi...',
  ],
  done: ['Hotovo!', 'Dokončeno!', 'Úspěšně zpracováno!'],
};

function getPhaseFromUrls(urls: UrlStatusUpdate[]): Phase {
  if (urls.length === 0) {
    return 'searching';
  }

  const statuses = urls.map(u => u.status);
  const allDone = statuses.every(s => s === DocumentStatus.Done);
  const anyEmbedding = statuses.some(
    s => s === DocumentStatus.Embedding || s === DocumentStatus.Done,
  );
  const anyParsed = statuses.some(
    s =>
      s === DocumentStatus.Parsed ||
      s === DocumentStatus.Embedding ||
      s === DocumentStatus.Done,
  );

  if (allDone) {
    return 'done';
  }

  if (anyEmbedding) {
    return 'embedding';
  }

  if (anyParsed) {
    return 'parsing';
  }

  return 'parsing';
}

export function StatusBar({ urls }: Props) {
  const [currentMessage, setCurrentMessage] = useState('');
  const [lockedPhase, setLockedPhase] = useState<Phase | null>(null);
  const phase = getPhaseFromUrls(urls);

  useEffect(() => {
    // Only pick a new message when phase changes
    if (lockedPhase !== phase) {
      const messages = phaseMessages[phase];
      const randomMessage =
        messages[Math.floor(Math.random() * messages.length)];
      setCurrentMessage(randomMessage);
      setLockedPhase(phase);
    }
  }, [phase, lockedPhase]);

  return (
    <div className='flex items-center gap-2 rounded-full bg-[#9c70ff0d] px-2 py-1'>
      <Loader2 className='size-2.5 animate-spin' />
      <span className='relative animate-[shimmer-text_2s_ease-out_infinite] bg-gradient-to-r from-[#8b5cf6] via-[#cec6f1] to-[#8b5cf6] bg-[length:200%_100%] bg-clip-text align-middle text-[11px] font-medium leading-[18px] text-transparent'>
        {currentMessage}
      </span>
    </div>
  );
}
